// src/pages/data/dataW/DataLG.jsx

import React, {useEffect, useState, useRef} from 'react';
import {sseRequest, httpRequest} from '@crema/services/auth/jwt-auth';
import DataUI from './DataUI';
import AppInfoView from '@crema/core/AppInfoView';
import {useDispatch} from 'react-redux';
import {fetchError, showMessage} from 'redux/actions';
import {useAuthUser} from '@crema/utility/AuthHooks';
import {format} from 'date-fns';
import {saveAs} from 'file-saver';

const DataLG = () => {
  const dispatch = useDispatch();

  // Pagination and Data State
  const [rowCount, setRowCount] = useState(50);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(0);
  const [dataToShow, setDataToShow] = useState([]);
  const [exportingData, setExportingData] = useState(false);
  const [exportingModal, setExportingModal] = useState(false);

  // Filter State
  const [filters, setFilters] = useState({
    country: '',
    dataSource: '',
    integrationSource: '',
    search: '',
    dateRange: [null, null],
    showNewData: false,
  });

  // Loading and Error States
  const [gettingData, setGettingData] = useState(false);
  const [selected, setSelected] = useState([]);
  const [doneStatusCount, setDoneStatusCount] = useState(0);
  const [allStatusCount, setAllStatusCount] = useState(0);
  const [errorStatusCount, setErrorStatusCount] = useState(0);

  // Modal States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [upLoadingData, setUpLoadingData] = useState(false);

  // Search and Selection States
  const [searchTerm, setSearchTerm] = useState('');
  const [customerId, setCustomerId] = useState(0);
  const [selecteCustomer, setSelecteCustomer] = useState(false);
  const [affiliateId, setAffiliateId] = useState([]);

  // Error Handling
  const [errorData, setErrorData] = useState([]);
  const [archiveData, setArchiveData] = useState({});
  const [uploadFinished, setUploadFinished] = useState(false); // New state to track upload completion

  // Domain States
  const [dominId, setDominId] = useState([]);
  const [domainIdSelected, setDomainIdSelected] = useState(null);
  const [domainSelected, setDomainSelected] = useState(false);
  const [domainBackendUrl, setDomainBackendUrl] = useState(null);

  // New Filter Options States
  const [scorcessOptions, setScorcessOptions] = useState([]);

  // Patch Edited State
  const [patchEdited, setPatchEdited] = useState({});

  // Lead History Modal State
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLeadId, setHistoryLeadId] = useState(null);
  const [historyIsDuplicate, setHistoryIsDuplicate] = useState(false);

  const user = useAuthUser();
  const [isFilterCardVisible, setIsFilterCardVisible] = useState(false); // default is visible

  // New State for disableDataSource Flag
  const [disableDataSource, setDisableDataSource] = useState(false);

  // appliedFilters is what's actually fetched; filters is the input/display state
  const [appliedFilters, setAppliedFilters] = useState({
    country: '',
    dataSource: '',
    integrationSource: '',
    search: '',
    dateRange: [null, null],
    showNewData: false,
  });

  // Request counter — only the latest request may update state
  const requestIdRef = useRef(0);

  // Guard to skip debounce on initial mount
  const [isFilterMounted, setIsFilterMounted] = useState(false);
  useEffect(() => {
    setIsFilterMounted(true);
  }, []);

  // Fetch filter options on mount (not tied to pagination)
  useEffect(() => {
    getFilterOptions();
  }, []);

  // THE ONLY effect that fetches data — fires when page, size, or applied filters change
  useEffect(() => {
    getData(pageNumber, pageSize, appliedFilters);
  }, [pageNumber, pageSize, appliedFilters]);

  // Filter debounce — batches page reset + appliedFilters update into ONE render
  useEffect(() => {
    if (!isFilterMounted) return;
    const timer = setTimeout(() => {
      setPageNumber(0);
      setAppliedFilters({...filters});
    }, 500);
    return () => clearTimeout(timer);
  }, [
    filters.search,
    filters.country,
    filters.dataSource,
    filters.integrationSource,
    filters.dateRange,
    filters.showNewData,
  ]);

  // Fetch domains when the component mounts
  useEffect(() => {
    const getDataTimeout = setTimeout(() => {
      getDomains(10, 0, searchTerm);
    }, 1000);
    return () => clearTimeout(getDataTimeout);
  }, [searchTerm]); // Added searchTerm as a dependency

  // Toggle the visibility of the filter card
  const toggleFilterCardVisibility = () => {
    setIsFilterCardVisible(!isFilterCardVisible);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      let date;

      // Handle different date formats
      if (typeof dateString === 'string') {
        // Try parsing as ISO string first
        if (dateString.includes('T') || dateString.includes('Z')) {
          date = new Date(dateString);
        } else if (dateString.includes('-')) {
          // Handle YYYY-MM-DD format
          date = new Date(dateString);
        } else if (dateString.includes('/')) {
          // Handle DD/MM/YYYY format
          const parts = dateString.split(/[/\s:]/);
          if (parts.length >= 3) {
            // Assume DD/MM/YYYY format
            date = new Date(
              parts[2],
              parts[1] - 1,
              parts[0],
              parts[3] || 0,
              parts[4] || 0,
            );
          } else {
            date = new Date(dateString);
          }
        } else {
          date = new Date(dateString);
        }
      } else if (dateString instanceof Date) {
        date = dateString;
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return 'Invalid Date';
      }
      return format(date, 'dd/MM/yyyy HH:mm');
    } catch (error) {
      console.warn('Date formatting error:', error, 'for date:', dateString);
      return 'Invalid Date';
    }
  };

  // Fetch Data with Filters
  const getData = async (pageNumber, pageSize, filterParams = filters) => {
    const thisRequestId = ++requestIdRef.current;
    setGettingData(true);
    setDisableDataSource(false);
    try {
      // Construct query string based on filter parameters
      let query = `data/freeData?pageNumber=${pageNumber}&pageSize=${pageSize}`;
      if (filterParams.customer)
        query += `&customer=${encodeURIComponent(filterParams.customer)}`;
      if (filterParams.country)
        query += `&country=${encodeURIComponent(filterParams.country)}`;
      if (filterParams.dataSource)
        query += `&dataSource=${encodeURIComponent(filterParams.dataSource)}`;
      if (filterParams.integrationSource)
        query += `&integrationSource=${encodeURIComponent(
          filterParams.integrationSource,
        )}`;
      if (filterParams.search)
        query += `&searchParams=${encodeURIComponent(filterParams.search)}`;
      if (filterParams.dateRange) {
        const [startDate, endDate] = filterParams.dateRange;
        const normalizeToDayIso = (d, isEndOfDay = false) => {
          if (!d) return '';
          try {
            const dd = d instanceof Date ? new Date(d) : new Date(d);
            if (isNaN(dd.getTime())) return '';
            if (isEndOfDay) {
              dd.setHours(23, 59, 59, 999);
            } else {
              dd.setHours(0, 0, 0, 0);
            }
            return dd.toISOString();
          } catch (_) {
            return '';
          }
        };
        const from = normalizeToDayIso(startDate, false);
        const to = normalizeToDayIso(endDate, true);
        if (from && to)
          query += `&fromDate=${encodeURIComponent(
            from,
          )}&toDate=${encodeURIComponent(to)}`;
      }
      if (filterParams.showNewData) query += `&showNewData=true`;

      console.log('Fetching data with query:', query);
      const result = await httpRequest(query.replace(/^\/+/, ''), {
        method: 'GET',
      });

      console.log('Data fetch result:', {
        dataLength: result.data.data?.length || 0,
        count: result.data.count,
        pageNumber,
        pageSize,
      });

      if (thisRequestId !== requestIdRef.current) return; // stale — a newer request is in flight
      setDataToShow(result.data.data || []);
      setRowCount(result.data.count || 0);
    } catch (e) {
      if (thisRequestId !== requestIdRef.current) return;
      console.error('Error fetching data:', e);
      dispatch(
        fetchError(
          e.response?.data?.message || e.message || 'Failed to fetch data',
        ),
      );
    } finally {
      if (thisRequestId === requestIdRef.current) setGettingData(false);
    }
  };

  // Fetch filter options from the backend
  const getFilterOptions = async () => {
    try {
      // Define the endpoint (adjust if you need to include a dynamic baseUrl)
      const endpoint = 'data/filterOptionsFree'; // Or `${baseUrl}data/filterOptions` if using a dynamic baseUrl

      // Make the GET request using httpRequest
      const response = await httpRequest(endpoint, {method: 'GET'});

      // Clean up response data for valid data sources
      const scorcessSet = new Set(response.data.data.dataSources);

      setScorcessOptions(
        [...scorcessSet].map((dataSource) => ({
          label: dataSource,
          key: dataSource,
        })),
      );
    } catch (error) {
      dispatch(fetchError(error.message));
    }
  };

  // Apply filters with pagination reset
  const handleApplyFilters = () => {
    setPageNumber(0);
    setAppliedFilters({...filters}); // triggers main effect — no direct getData call
  };

  // Clear filters and reset pagination
  const handleClearFilters = () => {
    const empty = {
      country: '',
      dataSource: '',
      integrationSource: '',
      search: '',
      dateRange: [null, null],
      showNewData: false,
    };
    setFilters(empty);
    setAppliedFilters(empty); // triggers main effect — no direct getData call
    setPageNumber(0);
  };

  // Handle Modal Close
  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelecteCustomer(false);
    setDomainSelected(false);
    setDomainIdSelected(null);

    // Only clear archiveData if not uploading
    if (!upLoadingData) {
      setArchiveData({});
    }

    // Refresh data after closing the sending modal
    getData(pageNumber, pageSize, appliedFilters);
  };

  // Handle Domain Selection Change
  // Handle Domain Selection Change
  const handleDomainChange = (_, valueSelected) => {
    if (valueSelected && valueSelected.key) {
      setDomainSelected(true);

      // Find the selected domain's backend URL
      const selectedDomain = dominId.find(
        (domain) => domain.key === valueSelected.key,
      );

      if (selectedDomain) {
        setDomainBackendUrl(selectedDomain.url); // Store the selected domain's backend URL

        // Set the domain key to domainIdSelected as requested
        setDomainIdSelected(selectedDomain.key);
        console.log('setDomainIdSelected');
        console.log(domainIdSelected);
      }

      setCustomerId(null); // Clear customer ID
      getCustomers(1000, 0, searchTerm, valueSelected.key); // Fetch customers with the selected domain ID
      onDropDownChange(_, valueSelected);
      setSelecteCustomer(false);
    } else {
      setDomainSelected(false);
      setDomainIdSelected(null);
      setDomainBackendUrl(null); // Clear the backend URL
      setCustomerId(null); // Clear the customer ID
      setAffiliateId([]); // Clear customer list if no domain is selected
      onDropDownChange(_, valueSelected);
      setSelecteCustomer(false); // Hide AI dropdown
    }
  };

  // Handler for dropdown changes (updated)
  const handleChange = (field) => (event, value) => {
    // Only store the key (string) in filters, not the entire object
    setFilters((prev) => ({
      ...prev,
      [field]: value?.key || '', // Safely handle null/undefined
    }));
  };

  // Helper function to format error messages into readable text
  const formatErrorMessage = (errorMessage) => {
    if (!errorMessage) return 'An error occurred';

    // Check for duplicate entry error with ID
    const duplicateIdMatch = errorMessage.match(/Duplicate lead ID:\s*(\d+)/);

    if (duplicateIdMatch) {
      const duplicateId = duplicateIdMatch[1];
      return `This data is duplicate of Lead ID: ${duplicateId}. Please check the existing lead before updating.`;
    }

    // Check for generic duplicate entry error
    if (
      errorMessage.includes('Duplicate entry') ||
      errorMessage.includes('duplicate')
    ) {
      return 'This data already exists in the system. Please check for duplicates.';
    }

    // Remove technical prefixes like "CANNOT_UPDATE_DATA:"
    let cleanMessage = errorMessage.replace(/^[A-Z_]+:\s*/g, '');

    // Simplify MySQL error messages
    cleanMessage = cleanMessage.replace(/for key ['"][\w.]+['"]/gi, '');
    cleanMessage = cleanMessage.replace(
      /Duplicate entry ['"][^'"]*['"]/gi,
      'Duplicate data found',
    );

    return cleanMessage;
  };

  // Handle Editing Data
  const onEditCommit = (rowId, editData = null) => {
    const flatingObject = {};
    let neededObject;

    // Use provided editData if available, otherwise use patchEdited state
    if (editData && editData[rowId]) {
      neededObject = editData[rowId];
    } else {
      neededObject = patchEdited[rowId];
    }

    // Check if neededObject exists and is not null/undefined
    if (!neededObject || typeof neededObject !== 'object') {
      console.warn('No edit data found for row ID:', rowId);
      return;
    }

    Object.keys(neededObject).forEach((key) => {
      flatingObject[key] = neededObject[key].value;
    });

    flatingObject.id = rowId;

    httpRequest('data', {
      method: 'PUT',
      body: flatingObject,
    })
      .then(() => {
        dispatch(
          showMessage(
            'Successfully updated data with ID = ' + flatingObject.id,
          ),
        );
        // Refresh the data table after successful update
        getData(pageNumber, pageSize, appliedFilters);
      })
      .catch((e) => {
        // Extract the actual error message from the backend response
        const errorMessage =
          e.response?.data?.message || e.message || 'Failed to update data';
        // Format the error message to be user-friendly
        const readableMessage = formatErrorMessage(errorMessage);
        dispatch(fetchError(readableMessage));
      });
  };

  // Handle Row Selection
  const handleOnSelect = (values) => {
    setSelected(values);
  };

  // Handle Upload Submission
  const onUploadSubmitting = () => {
    setUpLoadingData(true);

    // Use the selected domain's backend URL
    const baseUrl =
      domainBackendUrl ||
      process.env.REACT_APP_API_BASE_URL ||
      'https://be.blackdawn.info';

    console.log('Starting upload with baseUrl:', baseUrl);

    sseRequest(`data/send-data/${customerId}`, {
      contentType: 'application/json',
      body: JSON.stringify({dataIds: selected}),
      baseUrl: baseUrl, // Pass the backend URL here
      onmessage: (message) => {
        console.log('Received message:', message); // Log received message

        if (message.event.includes('doneUploadAPI')) {
          setAllStatusCount((prev) => prev + 1);
          setDoneStatusCount((prev) => prev + 1);
        } else if (message.event.includes('errorUploadAPI')) {
          setErrorStatusCount((prev) => prev + 1);
          setAllStatusCount((prev) => prev + 1);
          setErrorData((prev) => [...prev, remanageDataError(message.data)]);
        }

        // Update archiveData by accumulating all id-status pairs
        setArchiveData((prev) => {
          const {id, status} = remanageDataArchive(message.data);
          return {...prev, [id]: {id, status}};
        });
      },
      onclose: () => {
        console.log('Upload process closed.');
        setUpLoadingData(false);
        setUploadFinished(true); // Set uploadFinished to true
      },
      onerror: (error) => {
        console.log('Upload error:', error);
        dispatch(fetchError('Failed: ' + error?.message));
        setUpLoadingData(false);
      },
    });
  };

  // useEffect to send mapped archiveData to the API
  useEffect(() => {
    if (uploadFinished) {
      console.log('Final archiveData:', archiveData); // Log final archiveData

      // Define the API endpoint using backticks for template literals
      const apiEndpoint = `archive/add-api`;
      console.log('Sending archiveData to:', apiEndpoint);

      // Map archiveData to the required format with "id" and "status"
      const mappedArchiveData = Object.values(archiveData).map((entry) => ({
        id: entry.id,
        status: entry.status,
      }));

      // Prepare the data to send (ensure it matches the API's expected format)
      const dataToSend = {
        archive: mappedArchiveData, // Adjusted archive structure
        customer_id: customerId,
        domain_id: domainIdSelected,
      };
      console.log('Data to send:', dataToSend);

      // Make the POST request using httpRequest
      httpRequest(apiEndpoint, {
        method: 'POST',
        body: dataToSend,
      })
        .then((response) => {
          console.log('API response:', response);

          // Handle successful response
          dispatch(showMessage('Archive data successfully sent.'));
          // Reset archiveData and uploadFinished
          setArchiveData({});
          setUploadFinished(false);
        })
        .catch((error) => {
          console.log('API error:', error);
          // Handle errors
          dispatch(fetchError('Failed to send archive data: ' + error.message));
        });
    }
  }, [uploadFinished, dispatch]);

  // Fetch Domains
  const getDomains = async (pageSize, pageNumber, search) => {
    try {
      const url = `domain/search?pageSize=${pageSize}&pageNumber=${pageNumber}&term=${encodeURIComponent(
        search || '',
      )}`;
      const response = await httpRequest(url, {method: 'GET'});
      setDominId(
        response.data.data.map((item) => ({
          label: `${item.domain_name}`,
          key: item.id,
          url: item.domain_backend_url, // Store the domain's backend URL
        })),
      );
    } catch (e) {
      dispatch(fetchError(e.message));
    }
  };

  // Handle Data Deletion
  const deleteData = () => {
    if (!selected || selected.length === 0) {
      dispatch(fetchError('Please select data to delete'));
      return;
    }

    console.log('Deleting data with IDs:', selected);
    setUpLoadingData(true);

    httpRequest('data/delete-all', {
      method: 'POST',
      body: {ids: selected},
    })
      .then((response) => {
        console.log('Delete response:', response);

        // Show success message
        dispatch(
          showMessage(
            `Successfully moved ${selected.length} record(s) to deleted data table`,
          ),
        );

        // Clear selected items
        setSelected([]);

        // Refresh data with current filters
        console.log('Refreshing data after deletion...');
        getData(pageNumber, pageSize, appliedFilters);
      })
      .catch((e) => {
        console.error('Delete error:', e);
        const errorMessage =
          e.response?.data?.message || e.message || 'Failed to delete data';
        dispatch(fetchError(errorMessage));
      })
      .finally(() => {
        setUpLoadingData(false);
      });
  };

  // Remanage Error Data
  const remanageDataError = (string) => {
    const jsonParsed = JSON.parse(string); // parses entire string to object

    let data;

    if (typeof jsonParsed.data === 'string') {
      try {
        data = JSON.parse(jsonParsed.data); // parses string if needed
      } catch {
        data = jsonParsed.data;
      }
    } else {
      data = jsonParsed.data; // already an object
    }

    // attach error info safely
    data.error = jsonParsed.error || jsonParsed.originalErrorData || null;

    return data;
  };

  // Remanage Archive Data
  const remanageDataArchive = (input) => {
    try {
      console.log('Incoming input:', input);

      // Ensure input is parsed
      const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;

      // Extract and parse inner `data`
      const rawData = parsedInput.data;
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;

      // Safely extract ID and determine status
      const id = data?.id || 'Unknown ID';
      const status =
        parsedInput.error || (id === 'Unknown ID' ? 'Error' : 'Success');

      console.log('Parsed Archive Data:', {id, status});
      return {id, status};
    } catch (error) {
      console.error('Error parsing archive data:', error);
      return {id: 'Unknown ID', status: 'Error'};
    }
  };

  // Fetch Customers
  const getCustomers = async (pageSize, pageNumber, search, dominId = null) => {
    try {
      const url = `customer/search-with-ingration-only?pageSize=${pageSize}&pageNumber=${pageNumber}&term=${encodeURIComponent(
        search || '',
      )}${dominId ? `&domainId=${encodeURIComponent(dominId)}` : ''}`;
      const response = await httpRequest(url, {method: 'GET'});
      setAffiliateId(
        response.data.data.data.map((item) => ({
          label: `${item.user_name}${
            item.integration_type === 'trackbox'
              ? ' - ' + JSON.parse(item.additional_data_integration)?.ai
              : ''
          }`,
          key: item.id,
        })),
      );
    } catch (e) {
      dispatch(fetchError(e.message));
    }
  };

  // Handler for search input changes
  const handleSearchChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      search: event.target.value,
    }));
  };

  // Handler for Date Range change
  const handleDateRangeChange = (newValue) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: newValue,
    }));
  };

  // Handle Customer Dropdown Change
  const onDropDownChange = (_, valueSelected) => {
    if (valueSelected && valueSelected.key) {
      setCustomerId(valueSelected.key);
      setSelecteCustomer(true);
    } else {
      // Handle the case when the value is cleared (valueSelected is null or undefined)
      setCustomerId(null);
      setSelecteCustomer(false); // or any other default or fallback behavior you want when the value is cleared
    }
  };

  // Handle Dropdown Search
  const onDropDownSearch = (searchField) => {
    setSearchTerm(searchField.target.value);
  };

  // Handle Clearing Upload Modal Status
  const handleClear = () => {
    setErrorStatusCount(0);
    setAllStatusCount(0);
    setErrorData([]);
    setDoneStatusCount(0);
  };
  const exportingModalOpen = () => {
    setExportingModal(true);
  };

  const exportingModalClose = () => {
    setExportingModal(false);
  };

  const handleGenerateReport = async (reportId) => {
    try {
      const response = await exportDataToExcle(reportId); // This should return the response object

      // Since the response is already parsed, directly destructure the needed values
      const {data, fileName} = response; // Get JSON data directly

      // Create a Blob from the Base64 string
      const byteCharacters = atob(data); // Decode the Base64 string
      const byteNumbers = new Uint8Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([byteNumbers], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Use FileSaver to save the blob as a file
      saveAs(blob, fileName);
      console.log('Report downloaded successfully:', fileName);
    } catch (error) {
      console.error('Error generating report:', error);
      dispatch(fetchError(error.message)); // Display error message in the UI
    }
  };

  const exportDataToExcle = async () => {
    const filterParams = filters;
    setExportingData(true);

    // Construct base query
    let query = `data/export-data-free-to-excle`;
    const params = [];

    if (filterParams.customer)
      params.push(`customer=${encodeURIComponent(filterParams.customer)}`);
    if (filterParams.country)
      params.push(`country=${encodeURIComponent(filterParams.country)}`);
    if (filterParams.dataSource)
      params.push(`dataSource=${encodeURIComponent(filterParams.dataSource)}`);
    if (filterParams.integrationSource)
      params.push(
        `integrationSource=${encodeURIComponent(
          filterParams.integrationSource,
        )}`,
      );
    if (filterParams.search)
      params.push(
        `searchParamsData=${encodeURIComponent(filterParams.search)}`,
      );
    if (filterParams.dateRange) {
      const [startDate, endDate] = filterParams.dateRange;
      const normalizeToDayIso = (d, isEndOfDay = false) => {
        if (!d) return '';
        try {
          const dd = d instanceof Date ? new Date(d) : new Date(d);
          if (isNaN(dd.getTime())) return '';
          if (isEndOfDay) {
            dd.setHours(23, 59, 59, 999);
          } else {
            dd.setHours(0, 0, 0, 0);
          }
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };
      const from = normalizeToDayIso(startDate, false);
      const to = normalizeToDayIso(endDate, true);
      if (from && to) {
        params.push(`fromDate=${encodeURIComponent(from)}`);
        params.push(`toDate=${encodeURIComponent(to)}`);
      }
    }

    // Add parameters to the query string
    if (params.length) {
      query += `?${params.join('&')}`;
    }

    console.log('Final Query:', query);

    try {
      const response = await httpRequest(query, {method: 'GET'});
      return response.data; // The response should contain { data, fileName }
    } catch (error) {
      console.error('Error exporting data:', error);
      throw new Error(error.response?.data?.message || 'Failed to export data');
    } finally {
      setExportingData(false);
    }
  };
  // Handle Open Lead History
  const handleOpenLeadHistory = (row) => {
    setHistoryLeadId(row?.id);
    setHistoryIsDuplicate(false);
    setHistoryModalOpen(true);
  };

  // Handle Close Lead History
  const handleCloseLeadHistory = () => {
    setHistoryModalOpen(false);
    setHistoryLeadId(null);
    setHistoryIsDuplicate(false);
  };

  return (
    <>
      <AppInfoView />
      {/* Data UI */}
      <DataUI
        // New Props for Lead History
        historyModalOpen={historyModalOpen}
        historyLeadId={historyLeadId}
        historyIsDuplicate={historyIsDuplicate}
        handleOpenLeadHistory={handleOpenLeadHistory}
        handleCloseLeadHistory={handleCloseLeadHistory}
        dataToShow={dataToShow}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        rowCount={rowCount}
        dominId={dominId}
        onFilterChange={handleApplyFilters} // Changed to handleApplyFilters
        onEditCommit={onEditCommit}
        setPatchEdited={setPatchEdited}
        gettingData={gettingData}
        handleOnSelect={handleOnSelect}
        handleChange={handleChange}
        isFilterCardVisible={isFilterCardVisible}
        selected={selected}
        domainSelected={domainSelected}
        handleDomainChange={handleDomainChange}
        allStatusCount={allStatusCount}
        doneStatusCount={doneStatusCount}
        errorStatusCount={errorStatusCount}
        setShowUploadModal={setShowUploadModal}
        handleCloseModal={handleCloseModal}
        showUploadModal={showUploadModal}
        onUploadSubmitting={onUploadSubmitting}
        onDropDownChange={onDropDownChange}
        onDropDownSearch={onDropDownSearch}
        selecteCustomer={selecteCustomer}
        affiliateId={affiliateId}
        exportDataToExcle={handleGenerateReport}
        errorData={errorData}
        userRole={user.user.role} // Ensure this is an array
        upLoadingData={upLoadingData}
        handleClear={handleClear}
        deleteData={deleteData}
        toggleFilterCardVisibility={toggleFilterCardVisibility}
        disableDataSource={disableDataSource}
        // Pass filter-related props
        filters={filters}
        exportingData={exportingData}
        exportingModalClose={exportingModalClose}
        exportingModalOpen={exportingModalOpen}
        setFilters={setFilters}
        exportingModal={exportingModal}
        formatDate={formatDate}
        scorcessOptions={scorcessOptions}
        handleDateRangeChange={handleDateRangeChange}
        handleSearchChange={handleSearchChange}
        handleClearFilters={handleClearFilters}
        archiveData={archiveData} // Pass archiveData as a prop to eliminate linter warning
      />
    </>
  );
};

export default DataLG;
